"use client";

import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TimerIcon from "@mui/icons-material/TimerOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import WatchLaterIcon from "@mui/icons-material/AvTimer";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import { FilterOption } from "@/components/screens/ServiceDashboard/components/DashboardHeader";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetManagerialDashboard from "@/hooks/requests/dashboard/service/use-get-managerial-dashboard";
import useGetServiceQueues from "@/hooks/requests/service-queue/use-get-service-queues";
import useGetTags from "@/hooks/requests/tags/use-get-tags";
import useGetUsers from "@/hooks/requests/user/use-get-users";
import useGetWorkflows from "@/hooks/requests/wokflow/use-get-workflow";
import IServiceQueue from "@/interfaces/service-queue.interface";
import { ITag } from "@/interfaces/tag.interface";
import IUser from "@/interfaces/user.interface";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { ServiceDashboardMessages } from "@/messages/serviceDashboard/service-dashboard.messages";
import { ManagerialDailyPoint, ManagerialKpi } from "@/services/service-dashboard.service";
import { BreadCrumb } from "@/types/page.types";

import ManagerialHeader, { DateRangeValue } from "./components/ManagerialHeader";
import AiDonutTile from "./components/AiDonutTile";
import BarListCard, { ProgressBar } from "./components/BarListCard";
import ContactsTile from "./components/ContactsTile";
import KpiTile from "./components/KpiTile";
import TrendChartCard from "./components/TrendChartCard";
import VariationBadge from "./components/VariationBadge";

type ManagerialDashboardProps = {
  messages: ServiceDashboardMessages;
};

const COLORS = {
  blue: "#1e6ff0",
  green: "#2e9e4f",
  amber: "#f5b800",
  purple: "#8e3ad6",
  indigo: "#4752d9",
  cyan: "#16b1c9",
  orange: "#f08a24",
  red: "#e5483b",
};

const BACKLOG_COLORS: Record<string, string> = {
  "0-15": COLORS.green,
  "15-30": COLORS.amber,
  "30-60": COLORS.orange,
  ">60": COLORS.red,
};

const RANK_COLORS = [COLORS.blue, COLORS.orange, COLORS.green, COLORS.cyan, COLORS.purple];

const backlogLabel = (range: string): string =>
  range.startsWith(">") ? `+${range.slice(1)} min` : `${range} min`;

const slaColor = (value: number | null, target: number): string => {
  if (value === null) return COLORS.red;
  if (value >= target) return COLORS.green;
  if (value >= target - 3) return COLORS.amber;
  return COLORS.red;
};

const formatNumber = (value: number): string =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

const formatDecimal = (value: number): string =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
    value
  );

const formatPercent = (value: number): string =>
  `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value)}%`;

const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainingSeconds = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
  return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
};

const formatDay = (date: string): string => {
  const [, month, day] = date.slice(0, 10).split("-");
  return `${day}/${month}`;
};

/** Dia (YYYY-MM-DD) de uma data da API; datas com fuso são convertidas para o horário local. */
const toDayKey = (date: string): string =>
  /T.*(Z|[+-]\d{2}:?\d{2})$/.test(date) ? toIsoDay(new Date(date)) : date.slice(0, 10);

/** Lista todos os dias de start a end (inclusivos); sem intervalo válido usa os dias recebidos. */
const buildCalendar = (start: string, end: string, fallback: string[]): string[] => {
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  const cursor = new Date(sy, sm - 1, sd);
  const last = new Date(ey, em - 1, ed);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime()) || cursor > last) {
    return [...fallback].sort();
  }
  const days: string[] = [];
  while (cursor <= last && days.length < 400) {
    days.push(toIsoDay(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

const toIsoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const currentMonthRange = (): DateRangeValue => {
  const now = new Date();
  return {
    startDate: toIsoDay(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: toIsoDay(now),
  };
};

const formatPercent2 = (value: number): string =>
  `${new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    value
  )}%`;

type AppliedFilters = {
  queueIds: number[];
  userIds: number[];
  workflowIds: number[];
  tagNames: string[];
};

const EMPTY_FILTERS: AppliedFilters = { queueIds: [], userIds: [], workflowIds: [], tagNames: [] };

const optionalArray = <T,>(values: T[]): T[] | undefined => (values.length ? values : undefined);

export default function ManagerialDashboard({ messages }: ManagerialDashboardProps) {
  const { lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const isFullscreen = searchParams.get("fullscreen") === "1";
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const [selectedRange, setSelectedRange] = useState<DateRangeValue>(currentMonthRange);
  const [selectedQueues, setSelectedQueues] = useState<FilterOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<FilterOption[]>([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState<FilterOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<FilterOption[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(EMPTY_FILTERS);

  const { data, isLoading, isRefreshing, error, refresh } = useGetManagerialDashboard({
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    queueIds: optionalArray(appliedFilters.queueIds),
    userIds: optionalArray(appliedFilters.userIds),
    workflowIds: optionalArray(appliedFilters.workflowIds),
    tagNames: optionalArray(appliedFilters.tagNames),
    autoRefresh,
  });

  useEffect(() => {
    if (data && !isRefreshing) setLastUpdatedAt(new Date());
  }, [data, isRefreshing]);

  useEffect(() => {
    if (!isFullscreen || !document.documentElement.requestFullscreen) return;
    document.documentElement.requestFullscreen().catch(() => undefined);
  }, [isFullscreen]);

  const handleFullscreen = () => {
    const url = `${ROUTE_URLS.serviceManagerialDashboard(lang)}?fullscreen=1`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const { data: queuesData } = useGetServiceQueues({ pageNumber: 1, pageSize: 150 });
  const { data: usersData } = useGetUsers({ pageNumber: 1, pageSize: 150, filters: [] });
  const { data: workflowsData } = useGetWorkflows({ pageNumber: 1, pageSize: 150, filters: [] });
  const { data: tagsData } = useGetTags({ pageNumber: 1, pageSize: 150, filters: [] });

  const queueOptions: FilterOption[] = useMemo(
    () =>
      (queuesData?.items ?? []).map((queue: IServiceQueue) => ({ id: queue.id, name: queue.name })),
    [queuesData]
  );
  const userOptions: FilterOption[] = useMemo(
    () => (usersData?.items ?? []).map((user: IUser) => ({ id: user.id, name: user.name })),
    [usersData]
  );
  const workflowOptions: FilterOption[] = useMemo(
    () =>
      (workflowsData?.items ?? []).map((workflow: IWorkflow) => ({
        id: workflow.id,
        name: workflow.name,
      })),
    [workflowsData]
  );
  const tagOptions: FilterOption[] = useMemo(
    () => (tagsData?.items ?? []).map((tag: ITag) => ({ id: tag.id, name: tag.name })),
    [tagsData]
  );

  const handleApplyFilters = () => {
    setAppliedFilters({
      queueIds: selectedQueues.map((item) => item.id),
      userIds: selectedUsers.map((item) => item.id),
      workflowIds: selectedWorkflows.map((item) => item.id),
      tagNames: selectedTags.map((item) => item.name),
    });
  };

  const handleClearFilters = () => {
    setSelectedQueues([]);
    setSelectedUsers([]);
    setSelectedWorkflows([]);
    setSelectedTags([]);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.serviceManagerialDashboard(lang) },
    { title: messages.sub_customer_service, href: ROUTE_URLS.serviceManagerialDashboard(lang) },
    { title: messages.managerialBreadCrumb, href: ROUTE_URLS.serviceManagerialDashboard(lang) },
  ];

  const errorInfo = {
    shouldLoadError: !data && !!error && !isLoading,
    title: messages.errorToLoad,
    description: messages.noDataMessage,
    alt: messages.notFoundAlt,
  };

  // Monta o calendário completo do período escolhido e encaixa cada ponto
  // da API no seu dia, para que o tooltip mostre sempre a data correta.
  const dailyByDay = new Map(
    (data?.daily ?? []).map((point) => [toDayKey(point.date), point] as const)
  );
  const calendarDays = buildCalendar(
    selectedRange.startDate,
    selectedRange.endDate,
    Array.from(dailyByDay.keys())
  );
  const daily = calendarDays.map((day) => dailyByDay.get(day) ?? null);
  const pickDaily = <T,>(fn: (point: ManagerialDailyPoint) => T | null) =>
    daily.map((point) => (point ? fn(point) : null));
  const dayLabels = calendarDays.map(formatDay);
  const weekdayNames = messages.weekdayShortNames.split(",");
  const contactsByTag = (data?.contactsByTag ?? []).slice(0, 5);

  const gridSx = (columns: string) => ({
    display: "grid",
    gap: 1.25,
    gridTemplateColumns: { xs: "1fr", lg: columns },
  });

  const aiPercentSeries = pickDaily((point) => {
    const total = point.aiConversations + point.humanConversations;
    return total ? (point.aiConversations / total) * 100 : null;
  });
  const humanPercentSeries = aiPercentSeries.map((value) => (value === null ? null : 100 - value));

  const kpis: {
    title: string;
    data: ManagerialKpi | undefined;
    format: (value: number) => string;
    icon: React.ReactNode;
    color: string;
    suffix?: string;
    lowerIsBetter?: boolean;
  }[] = [
    {
      title: messages.managerialNewConversationsTitle,
      data: data?.newConversations,
      format: formatNumber,
      icon: <ChatBubbleOutlineIcon />,
      color: COLORS.blue,
    },
    {
      title: messages.managerialClosedConversationsTitle,
      data: data?.closedConversations,
      format: formatNumber,
      icon: <CheckCircleOutlineIcon />,
      color: COLORS.green,
    },
    {
      title: messages.managerialSlaTitle,
      data: data?.responseSlaPercent,
      format: formatPercent,
      icon: <TrackChangesIcon />,
      color: COLORS.amber,
    },
    {
      title: messages.managerialFrtTitle,
      data: data?.firstResponseTimeSeconds,
      format: formatDuration,
      icon: <WatchLaterIcon />,
      color: COLORS.purple,
      lowerIsBetter: true,
    },
    {
      title: messages.managerialTmaTitle,
      data: data?.averageHandlingTimeSeconds,
      format: formatDuration,
      icon: <TimerIcon />,
      color: COLORS.indigo,
      lowerIsBetter: true,
    },
    {
      title: messages.resolutionRateTitle,
      data: data?.resolutionRatePercent,
      format: formatPercent2,
      icon: <TaskAltIcon />,
      color: COLORS.green,
      suffix:
        data?.resolvedConversations?.value === null ||
        data?.resolvedConversations?.value === undefined
          ? undefined
          : `(${formatNumber(data.resolvedConversations.value)} ${messages.resolvedConversationsLabel})`,
    },
    {
      title: messages.csatSatisfactionTitle,
      data: data?.csat,
      format: formatDecimal,
      icon: <StarBorderIcon />,
      color: COLORS.amber,
      suffix: "/ 5",
    },
  ];

  const slaTarget = data?.slaTargetPercent ?? 90;
  const csatTarget = data?.csatTarget ?? 4;

  return (
    <PageLayout
      messages={messages}
      title={isFullscreen ? undefined : messages.managerialPageTitle}
      hideBoldTitle
      breadCrumbs={isFullscreen ? [] : BREAD_CRUMBS}
      fullscreen={isFullscreen}
    >
      <PageContainer>
        <ManagerialHeader
          messages={messages}
          selectedRange={selectedRange}
          onChangeRange={setSelectedRange}
          filters={[
            {
              label: messages.queueFilter,
              options: queueOptions,
              value: selectedQueues,
              onChange: setSelectedQueues,
            },
            {
              label: messages.attendantFilter,
              options: userOptions,
              value: selectedUsers,
              onChange: setSelectedUsers,
            },
            {
              label: messages.workflowFilter,
              options: workflowOptions,
              value: selectedWorkflows,
              onChange: setSelectedWorkflows,
            },
            {
              label: messages.tagFilter,
              options: tagOptions,
              value: selectedTags,
              onChange: setSelectedTags,
            },
          ]}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          onRefresh={refresh}
          isRefreshing={isRefreshing}
          autoRefresh={autoRefresh}
          onToggleAutoRefresh={setAutoRefresh}
          lastUpdatedAt={lastUpdatedAt}
          isFullscreen={isFullscreen}
          onFullscreen={handleFullscreen}
        />

        <MDBox display="flex" flexDirection="column" gap={1.25}>
          <MDBox
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(20, 1fr)",
              },
              "@media (min-width: 1200px)": {
                "& > :nth-child(-n+5)": { gridColumn: "span 4" },
                "& > :nth-child(n+6)": { gridColumn: "span 5" },
              },
            }}
          >
            {kpis.map((kpi) => (
              <KpiTile
                key={kpi.title}
                title={kpi.title}
                value={
                  kpi.data?.value === null || kpi.data?.value === undefined
                    ? "--"
                    : kpi.format(kpi.data.value)
                }
                suffix={kpi.suffix}
                icon={kpi.icon}
                color={kpi.color}
                variation={kpi.data?.variation}
                isPoints={kpi.data?.isPointVariation}
                lowerIsBetter={kpi.lowerIsBetter}
                caption={messages.vsPreviousMonthLabel}
                series={kpi.data?.series ?? []}
                isLoading={isLoading}
                isRefreshing={isRefreshing}
              />
            ))}
            <AiDonutTile
              title={messages.aiVsHumanTitle}
              aiLabel={messages.aiLabel}
              humanLabel={messages.humanLabel}
              aiCount={data?.aiConversations ?? 0}
              humanCount={data?.humanConversations ?? 0}
              variation={data?.aiSharePercent?.variation}
              caption={messages.vsPreviousMonthLabel}
              aiColor={COLORS.cyan}
              humanColor={COLORS.blue}
              isLoading={isLoading}
            />
            <ContactsTile
              title={messages.contactsTitle}
              attendedLabel={messages.attendedContactsLabel}
              newLabel={messages.newContactsLabel}
              attended={data?.attendedContacts}
              newContacts={data?.newContacts}
              caption={messages.vsPreviousMonthLabel}
              color={COLORS.indigo}
              formatValue={formatNumber}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
          </MDBox>

          <MDBox sx={gridSx("repeat(3, 1fr)")}>
            <TrendChartCard
              title={messages.conversationsEvolutionTitle}
              labels={dayLabels}
              series={[
                {
                  label: messages.newLabel,
                  values: pickDaily((point) => point.newConversations),
                  color: "info",
                },
                {
                  label: messages.closedLabel,
                  values: pickDaily((point) => point.closedConversations),
                  color: "success",
                },
              ]}
              valueFormatter={formatNumber}
              emptyLabel={messages.noDataMessage}
              height="10.5rem"
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <TrendChartCard
              title={messages.slaEvolutionPercentTitle}
              labels={dayLabels}
              series={[
                {
                  label: messages.slaColumn,
                  values: pickDaily((point) => point.slaPercent),
                  color: "primary",
                },
                {
                  label: `${messages.targetLabel} (${formatPercent(slaTarget)})`,
                  values: calendarDays.map(() => slaTarget),
                  color: "error",
                  dashed: true,
                },
              ]}
              valueFormatter={formatPercent}
              yMax={100}
              emptyLabel={messages.noDataMessage}
              height="10.5rem"
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <TrendChartCard
              title={messages.csatSatisfactionEvolutionTitle}
              labels={dayLabels}
              series={[
                {
                  label: "CSAT",
                  values: pickDaily((point) => point.csat),
                  color: "warning",
                },
                {
                  label: `${messages.targetLabel} (${formatDecimal(csatTarget)})`,
                  values: calendarDays.map(() => csatTarget),
                  color: "error",
                  dashed: true,
                },
              ]}
              valueFormatter={formatDecimal}
              yMax={5}
              emptyLabel={messages.noRatingsLabel}
              height="10.5rem"
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
          </MDBox>

          <MDBox sx={gridSx("1.7fr 0.9fr 0.75fr 1fr")}>
            <TrendChartCard
              type="line"
              title={messages.entriesByHourTitle}
              labels={(data?.entriesByHour ?? []).map((item) => `${item.hour}h`)}
              series={[
                {
                  label: messages.newLabel,
                  values: (data?.entriesByHour ?? []).map((item) => item.count),
                  color: "primary",
                },
              ]}
              valueFormatter={formatNumber}
              emptyLabel={messages.noDataMessage}
              height="7.5rem"
              isLoading={isLoading}
              isRefreshing={isRefreshing}
              aside={
                <MDBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  gap={1}
                  pl={1.5}
                  minWidth="6.5rem"
                  sx={{ borderLeft: "1px solid", borderColor: "grey.200" }}
                >
                  {[
                    {
                      label: messages.insideBusinessHoursLabel,
                      value: data?.entriesInsideBusinessHours,
                      color: COLORS.cyan,
                    },
                    {
                      label: messages.outsideBusinessHoursLabel,
                      value: data?.entriesOutsideBusinessHours,
                      color: COLORS.blue,
                    },
                  ].map((item) => {
                    const raw =
                      typeof item.value === "object" && item.value !== null
                        ? item.value.value
                        : item.value;
                    return (
                      <MDBox key={item.label}>
                        <MDTypography variant="caption" color="text" display="block" lineHeight={1.2}>
                          {item.label}
                        </MDTypography>
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          lineHeight={1.2}
                          sx={{ color: item.color }}
                        >
                          {raw === null || raw === undefined ? "--" : formatNumber(Number(raw))}
                        </MDTypography>
                      </MDBox>
                    );
                  })}
                </MDBox>
              }
            />
            <TrendChartCard
              type="bar"
              title={messages.entriesByWeekdayTitle}
              labels={(data?.entriesByWeekday ?? []).map(
                (item) => weekdayNames[item.weekday - 1] ?? `${item.weekday}`
              )}
              series={[
                {
                  label: messages.newLabel,
                  values: (data?.entriesByWeekday ?? []).map((item) => item.count),
                  color: "primary",
                },
              ]}
              valueFormatter={formatNumber}
              emptyLabel={messages.noDataMessage}
              height="7.5rem"
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <BarListCard
              title={messages.backlogAverageTitle}
              gridTemplateColumns="4.5rem 1fr 2.5rem"
              rows={(data?.backlog ?? []).map((item) => ({
                key: item.range,
                cells: [
                  backlogLabel(item.range),
                  <ProgressBar
                    key="bar"
                    percent={item.percentage}
                    color={BACKLOG_COLORS[item.range] ?? COLORS.cyan}
                  />,
                  <MDTypography key="pct" variant="caption" fontWeight="medium" ml="auto">
                    {formatPercent(item.percentage)}
                  </MDTypography>,
                ],
              }))}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <TrendChartCard
              title={messages.aiVsHumanPercentTitle}
              labels={dayLabels}
              series={[
                { label: messages.aiLabel, values: aiPercentSeries, color: "primary" },
                { label: messages.humanLabel, values: humanPercentSeries, color: "info" },
              ]}
              valueFormatter={formatPercent}
              yMax={100}
              emptyLabel={messages.noDataMessage}
              height="7.5rem"
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
          </MDBox>

          <MDBox sx={gridSx("1.25fr 1fr 0.85fr 0.95fr 0.95fr")}>
            <BarListCard
              title={messages.slaByQueuePercentTitle}
              gridTemplateColumns="1.2fr 1.2fr 0.55fr 0.55fr 0.9fr"
              columns={[
                { header: messages.queueColumn },
                { header: "" },
                { header: messages.slaColumn, align: "right" },
                { header: messages.targetColumn, align: "right" },
                { header: messages.vsPreviousMonthLabel, align: "right" },
              ]}
              rows={(data?.slaByQueue ?? []).slice(0, 5).map((item) => ({
                key: item.name,
                cells: [
                  item.name,
                  <ProgressBar
                    key="bar"
                    percent={item.slaPercent ?? 0}
                    color={slaColor(item.slaPercent, item.targetPercent)}
                  />,
                  item.slaPercent === null ? "--" : formatPercent(item.slaPercent),
                  formatPercent(item.targetPercent),
                  <VariationBadge key="var" value={item.variation} isPoints />,
                ],
              }))}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <BarListCard
              title={messages.unreadRankingTitle}
              gridTemplateColumns="1.5fr 0.9fr 0.9fr"
              columns={[
                { header: messages.queueColumn },
                { header: messages.unreadMessagesColumn, align: "right" },
                { header: messages.vsPreviousMonthLabel, align: "right" },
              ]}
              rows={(data?.unreadByQueue ?? []).slice(0, 5).map((item, index) => ({
                key: item.name,
                cells: [
                  <MDBox key="name" display="flex" alignItems="center" gap={1} minWidth={0}>
                    <MDBox
                      width="1.1rem"
                      height="1.1rem"
                      minWidth="1.1rem"
                      borderRadius={"50%" as never}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: RANK_COLORS[index % RANK_COLORS.length],
                        color: "#fff",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {item.rank}
                    </MDBox>
                    <MDTypography variant="caption" fontWeight="medium" color="dark" noWrap>
                      {item.name}
                    </MDTypography>
                  </MDBox>,
                  formatNumber(item.count),
                  <VariationBadge key="var" value={item.variation} lowerIsBetter />,
                ],
              }))}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <BarListCard
              title={messages.contactsByTagTitle}
              gridTemplateColumns="1.6fr 0.8fr"
              columns={[
                { header: messages.tagColumn },
                { header: messages.contactsColumn, align: "right" },
              ]}
              rows={contactsByTag.map((item, index) => ({
                key: item.name,
                cells: [
                  <MDBox key="name" display="flex" alignItems="center" gap={1} minWidth={0}>
                    <MDBox
                      width="1.1rem"
                      height="1.1rem"
                      minWidth="1.1rem"
                      borderRadius={"50%" as never}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: RANK_COLORS[index % RANK_COLORS.length],
                        color: "#fff",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </MDBox>
                    <MDTypography variant="caption" fontWeight="medium" color="dark" noWrap>
                      {item.name}
                    </MDTypography>
                  </MDBox>,
                  formatNumber(item.count),
                ],
              }))}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <BarListCard
              title={messages.conversationsBySubjectTitle}
              gridTemplateColumns="1.6fr 0.8fr"
              columns={[
                { header: messages.subjectColumn },
                { header: messages.conversationsColumn, align: "right" },
              ]}
              rows={(data?.conversationsBySubject ?? []).map((item) => ({
                key: item.name,
                cells: [item.name, formatNumber(item.count)],
              }))}
              maxRowsHeight="7.5rem"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <BarListCard
              title={messages.contactsRankingTitle}
              gridTemplateColumns="1.6fr 0.8fr"
              columns={[
                { header: messages.nameColumn },
                { header: messages.contactsColumn, align: "right" },
              ]}
              rows={(data?.contactsRanking ?? []).slice(0, 10).map((item, index) => ({
                key: `${item.contactId}-${item.rank}`,
                cells: [
                  <MDBox key="name" display="flex" alignItems="center" gap={1} minWidth={0}>
                    <MDBox
                      width="1.1rem"
                      height="1.1rem"
                      minWidth="1.1rem"
                      borderRadius={"50%" as never}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: RANK_COLORS[index % RANK_COLORS.length],
                        color: "#fff",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {item.rank}
                    </MDBox>
                    <MDTypography variant="caption" fontWeight="medium" color="dark" noWrap>
                      {item.name}
                    </MDTypography>
                  </MDBox>,
                  formatNumber(item.count),
                ],
              }))}
              maxRowsHeight="7.5rem"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
          </MDBox>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
}
